
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Building2,
  LayoutDashboard,
  Palette,
  CreditCard,
  Table2,
  Settings,
  LogOut,
  ShieldCheck,
  ShoppingBag,
  UploadCloud,
  BookOpen,
  KeyRound,
} from "lucide-react";

import {
  getSuperAdminOverview,
  getSuperAdminUser,
  superAdminLogout,
} from "./superAdminApi";

import RestaurantManagement from "./RestaurantManagement";
import SubscriptionManagement from "./SubscriptionManagement";
import RestaurantExcelImport from "./RestaurantExcelImport";
import SuperAdminCatalog from "./SuperAdminCatalog";
import PrivilegedPasswordManagement from "./PrivilegedPasswordManagement";

import RecoveryResetPanel from "../securityRecovery/RecoveryResetPanel";
import PrivilegedAuthenticatorRecovery from "../securityRecovery/PrivilegedAuthenticatorRecovery";

import "./SuperAdmin.css";

const navigation = [
  { label: "Overview", icon: LayoutDashboard, ready: true },
  { label: "Restaurants", icon: Building2, ready: true },
  { label: "Catalog & Owners", icon: BookOpen, ready: true },
  { label: "Excel Import", icon: UploadCloud, ready: true },
  { label: "Themes", icon: Palette, ready: false },
  { label: "Table Allocation", icon: Table2, ready: false },
  { label: "Subscriptions", icon: CreditCard, ready: true },
  { label: "Authenticator Recovery", icon: ShieldCheck, ready: true },
  { label: "Privileged Passwords", icon: KeyRound, ready: true },
  { label: "Settings", icon: Settings, ready: false },
];

export default function SuperAdminDashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [section, setSection] = useState("Overview");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    Promise.all([
      getSuperAdminUser(),
      getSuperAdminOverview(),
    ])
      .then(([current, data]) => {
        if (active) {
          setUser(current.user);
          setOverview(data);
        }
      })
      .catch((err) => {
        if (!active) return;

        if (err.status === 401 || err.status === 403) {
          navigate("/superadmin/login", { replace: true });
        } else {
          setError(err.message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await superAdminLogout();
      navigate("/superadmin/login", { replace: true });
    } catch (err) {
      setError(err.message);
    }
  }

  const stats = overview?.stats;

  const tiles = [
    {
      label: "Total restaurants",
      value: stats?.total_restaurants ?? "—",
      icon: Building2,
    },
    {
      label: "Active restaurants",
      value: stats?.active_restaurants ?? "—",
      icon: ShieldCheck,
    },
    {
      label: "Total orders",
      value: stats?.total_orders ?? "—",
      icon: ShoppingBag,
    },
    {
      label: "Registered tables",
      value: stats?.total_tables ?? "—",
      icon: Table2,
    },
  ];

  function content() {
    if (loading) {
      return <p>Loading dashboard…</p>;
    }

    if (section === "Restaurants") {
      return <RestaurantManagement />;
    }

    if (section === "Catalog & Owners") {
      return <SuperAdminCatalog />;
    }

    if (section === "Excel Import") {
      return <RestaurantExcelImport />;
    }

    if (section === "Subscriptions") {
      return <SubscriptionManagement />;
    }

    if (section === "Authenticator Recovery") {
      return (
        <>
          <PrivilegedAuthenticatorRecovery mode="platform" />

          <div style={{ marginTop: "32px" }}>
            <RecoveryResetPanel mode="platform" />
          </div>
        </>
      );
    }

    if (section === "Privileged Passwords") {
      return <PrivilegedPasswordManagement />;
    }

    if (!overview) {
      return <p>Dashboard information unavailable.</p>;
    }

    const restaurants = overview.restaurants || [];

    return (
      <>
        <section className="sa-stats">
          {tiles.map((tile) => {
            const Icon = tile.icon;

            return (
              <article className="sa-stat" key={tile.label}>
                <div className="sa-stat-top">
                  <span>{tile.label}</span>
                  <Icon size={20} />
                </div>

                <strong>{tile.value}</strong>
              </article>
            );
          })}
        </section>

        <section className="sa-panel">
          <div className="sa-panel-head">
            <div>
              <h2>Restaurants</h2>

              <p className="sa-muted">
                Overview. Use Restaurants to manage records.
              </p>
            </div>

            <span>{restaurants.length} shown</span>
          </div>

          <div className="sa-table-scroll">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Restaurant</th>
                  <th>Theme</th>
                  <th>Status</th>
                  <th>Table allocation</th>
                </tr>
              </thead>

              <tbody>
                {restaurants.length ? (
                  restaurants.map((restaurant) => (
                    <tr key={restaurant.id}>
                      <td>
                        <strong>{restaurant.name}</strong>

                        <small>
                          #{restaurant.id} · {restaurant.slug}
                        </small>
                      </td>

                      <td>{restaurant.theme}</td>

                      <td>
                        <span
                          className={`sa-status ${
                            restaurant.is_active ? "on" : "off"
                          }`}
                        >
                          {restaurant.is_active
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td>
                        {restaurant.table_limit} total

                        <small>
                          {restaurant.included_tables} included
                        </small>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No restaurants yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {stats?.total_restaurants >
            overview.restaurants_limit && (
            <p className="sa-muted">
              Showing first {overview.restaurants_limit} restaurants.
            </p>
          )}
        </section>
      </>
    );
  }

  return (
    <div className="sa-shell">
      <aside className="sa-sidebar">
        <div className="sa-brand">
          <span className="sa-brand-icon">◆</span>

          <div>
            Platform Admin
            <small>Control Center</small>
          </div>
        </div>

        <div className="sa-nav-caption">WORKSPACE</div>

        <nav aria-label="Super Admin navigation">
          {navigation.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.label}
                className={`sa-nav-item ${
                  section === item.label ? "selected" : ""
                } ${!item.ready ? "disabled" : ""}`}
                onClick={() => item.ready && setSection(item.label)}
                role={item.ready ? "button" : undefined}
                tabIndex={item.ready ? 0 : undefined}
                onKeyDown={(event) => {
                  if (
                    item.ready &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    setSection(item.label);
                  }
                }}
                aria-disabled={!item.ready}
                title={
                  item.ready
                    ? item.label
                    : "Coming in a later stage"
                }
              >
                <Icon size={18} />

                <span>{item.label}</span>

                {!item.ready && <small>Soon</small>}
              </div>
            );
          })}
        </nav>

        <div className="sa-sidebar-bottom">
          <div className="sa-user">
            {user?.username || "Administrator"}
            <small>Platform superuser</small>
          </div>

          <button onClick={logout} className="sa-logout">
            <LogOut size={17} />
            Log out
          </button>
        </div>
      </aside>

      <main className="sa-content">
        <header className="sa-header">
          <div>
            <p className="sa-eyebrow">
              PLATFORM CONTROL CENTER
            </p>

            <h1>
              {section === "Overview" ? "Dashboard" : section}
            </h1>

            <p className="sa-muted">
              Manage your FoodHorizon restaurant network.
            </p>
          </div>

          <div className="sa-admin-pill">
            <ShieldCheck size={17} />
            Super Admin
          </div>
        </header>

        {error && (
          <p className="sa-error" role="alert">
            {error}
          </p>
        )}

        {content()}
      </main>
    </div>
  );
}
